/**
 * VenueBusinessType → schema.org `@type`.
 *
 * The value is chosen by an operator in the panel (or assigned by the panel's
 * resolver when they leave it alone) and stored on the venue record, so the
 * markup publishes what a human decided rather than what this page can infer
 * from a service list.
 *
 * The list is the mirror of
 * `saba-super-frontend  src/@core/constants/venue-business-type.ts`
 * and of the CHECK constraint on `venue.business_type`. Adding a type means
 * changing all three.
 *
 * Types that schema.org does not place under `LocalBusiness` are published as a
 * pair — `["LocalBusiness", "VeterinaryCare"]` — so the business stays a local
 * business without losing what it actually is. Trades that have no schema.org
 * class at all (dance school, dietitian, music school, driving school) take the
 * bare `LocalBusiness`, which is what Google's own mapping does for them.
 */
const SCHEMA_TYPES: Record<string, string | string[]> = {
  exercise_gym: "ExerciseGym",
  health_club: "HealthClub",
  sports_club: "SportsClub",
  pilates_studio: "ExerciseGym",
  yoga_studio: "ExerciseGym",
  dance_school: "LocalBusiness",
  restaurant: "Restaurant",
  cafe: "CafeOrCoffeeShop",
  bakery: "Bakery",
  bar_or_pub: "BarOrPub",
  beauty_salon: "BeautySalon",
  hair_salon: "HairSalon",
  day_spa: "DaySpa",
  medical_clinic: "MedicalClinic",
  // Same type these venues published as `medical_clinic` before SAB-362 gave
  // them their own value.
  physiotherapist: "MedicalClinic",
  veterinary_care: ["LocalBusiness", "VeterinaryCare"],
  preschool: ["LocalBusiness", "Preschool"],
  child_care: "ChildCare",
  dietitian: "LocalBusiness",
  music_school: "LocalBusiness",
  art_school: "LocalBusiness",
  driving_school: "LocalBusiness",
  event_venue: "LocalBusiness",
  local_business: "LocalBusiness",
};

/**
 * The `@type` for a stored business type, or `undefined` when the venue has
 * none — an unset field and a value this build does not know about are both
 * "no answer", and the caller falls back to reading the venue's own data
 * instead of publishing a type nobody chose.
 */
export function getSchemaTypeForBusinessType(
  businessType: unknown,
): string | string[] | undefined {
  if (typeof businessType !== "string") return undefined;

  return SCHEMA_TYPES[businessType];
}

export { SCHEMA_TYPES };

export const PHYSIOTHERAPIST_BUSINESS_TYPE = "physiotherapist";

/**
 * Physiotherapist venues (SAB-362) carry no comments / questions, discounts,
 * campaigns, quote or company discount CTAs on their pages, no membership
 * badge on their cards, and never appear among the homepage's featured venues.
 */
export function isPhysiotherapistVenue(
  venue?: { business_type?: string | null } | null,
): boolean {
  return venue?.business_type === PHYSIOTHERAPIST_BUSINESS_TYPE;
}
